/**
 * Local Storage Service
 * Verwaltet lokale Datenspeicherung für Offline-Funktionalität
 */

import type { Product, SalesInvoice, ZReport, SyncError, AppSettings, AuthToken } from '../types';

const STORAGE_KEYS = {
  PRODUCTS: 'pos_products',
  INVOICES: 'pos_invoices',
  Z_REPORTS: 'pos_z_reports',
  SYNC_ERRORS: 'pos_sync_errors',
  SETTINGS: 'pos_settings',
  AUTH_TOKEN: 'pos_auth_token',
  USER: 'pos_user',
  LAST_SYNC: 'pos_last_sync'
} as const;

/**
 * Generic Storage Helper
 */
class StorageService {
  /**
   * Speichert Daten im LocalStorage
   */
  set<T>(key: string, value: T): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        console.warn('localStorage not available');
        return;
      }
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error(`Error saving to localStorage [${key}]:`, error);
    }
  }

  /**
   * Lädt Daten aus LocalStorage
   */
  get<T>(key: string): T | null {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return null;
      }
      const item = localStorage.getItem(key);
      if (item === null) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Error reading from localStorage [${key}]:`, error);
      return null;
    }
  }

  /**
   * Entfernt Daten aus LocalStorage
   */
  remove(key: string): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from localStorage [${key}]:`, error);
    }
  }

  /**
   * Prüft ob Key existiert
   */
  has(key: string): boolean {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    return localStorage.getItem(key) !== null;
  }

  /**
   * Leert alle POS-Daten
   */
  clear(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      this.remove(key);
    });
  }
}

// Singleton Instance
const storage = new StorageService();

/**
 * Products Storage
 */
export const productsStorage = {
  save: (products: Product[]): void => {
    storage.set(STORAGE_KEYS.PRODUCTS, products);
  },

  load: (): Product[] => {
    return storage.get<Product[]>(STORAGE_KEYS.PRODUCTS) || [];
  },

  clear: (): void => {
    storage.remove(STORAGE_KEYS.PRODUCTS);
  }
};

/**
 * Invoices Storage
 */
export const invoicesStorage = {
  save: (invoices: SalesInvoice[]): void => {
    storage.set(STORAGE_KEYS.INVOICES, invoices);
  },

  load: (): SalesInvoice[] => {
    return storage.get<SalesInvoice[]>(STORAGE_KEYS.INVOICES) || [];
  },

  add: (invoice: SalesInvoice): void => {
    const invoices = invoicesStorage.load();
    invoices.push(invoice);
    invoicesStorage.save(invoices);
  },

  update: (id: string, updates: Partial<SalesInvoice>): void => {
    const invoices = invoicesStorage.load();
    const index = invoices.findIndex((inv) => inv.id === id);
    if (index !== -1) {
      invoices[index] = { ...invoices[index], ...updates };
      invoicesStorage.save(invoices);
    }
  },

  getPending: (): SalesInvoice[] => {
    return invoicesStorage.load().filter((inv) => !inv.synced);
  },

  getSince: (sinceDate: string): SalesInvoice[] => {
    return invoicesStorage
      .load()
      .filter((inv) => inv.created_at && new Date(inv.created_at) >= new Date(sinceDate));
  },

  clear: (): void => {
    storage.remove(STORAGE_KEYS.INVOICES);
  }
};

/**
 * Z-Reports Storage
 */
export const zReportsStorage = {
  save: (reports: ZReport[]): void => {
    storage.set(STORAGE_KEYS.Z_REPORTS, reports);
  },

  load: (): ZReport[] => {
    return storage.get<ZReport[]>(STORAGE_KEYS.Z_REPORTS) || [];
  },

  add: (report: ZReport): void => {
    const reports = zReportsStorage.load();
    reports.push(report);
    zReportsStorage.save(reports);
  },

  getPending: (): ZReport[] => {
    return zReportsStorage.load().filter((report) => !report.synced);
  },

  getLast: (): ZReport | null => {
    const reports = zReportsStorage.load();
    if (reports.length === 0) return null;
    // Sortiere nach end_time (neueste zuerst)
    const sorted = [...reports].sort(
      (a, b) => new Date(b.end_time).getTime() - new Date(a.end_time).getTime()
    );
    return sorted[0];
  },

  update: (id: string, updates: Partial<ZReport>): void => {
    const reports = zReportsStorage.load();
    const index = reports.findIndex((r) => r.id === id);
    if (index !== -1) {
      reports[index] = { ...reports[index], ...updates };
      zReportsStorage.save(reports);
    }
  },

  clear: (): void => {
    storage.remove(STORAGE_KEYS.Z_REPORTS);
  }
};

/**
 * Sync Errors Storage
 */
export const syncErrorsStorage = {
  save: (errors: SyncError[]): void => {
    storage.set(STORAGE_KEYS.SYNC_ERRORS, errors);
  },

  load: (): SyncError[] => {
    return storage.get<SyncError[]>(STORAGE_KEYS.SYNC_ERRORS) || [];
  },

  add: (error: SyncError): void => {
    const errors = syncErrorsStorage.load();
    errors.push(error);
    syncErrorsStorage.save(errors);
  },

  clear: (): void => {
    storage.remove(STORAGE_KEYS.SYNC_ERRORS);
  }
};

/**
 * Settings Storage
 */
export const settingsStorage = {
  save: (settings: AppSettings): void => {
    storage.set(STORAGE_KEYS.SETTINGS, settings);
  },

  load: (): AppSettings => {
    const defaultSettings: AppSettings = {
      offline_mode: false,
      pos_id: '01',
      last_invoice_number: 0,
      last_invoice_date: '',
      frappe_api_key: undefined,
      frappe_api_secret: undefined,
      revenue_account: 'ACC-4000',
      cash_account: 'ACC-2700',
      sumup_account: 'ACC-2801',
      payment_fees_account: 'ACC-7790',
      sumup_fee_percentage: 1.39,
      test_mode: false
    };
    const loaded = storage.get<AppSettings>(STORAGE_KEYS.SETTINGS);
    if (loaded) {
      // Stelle sicher, dass Defaults gesetzt sind
      return {
        ...defaultSettings,
        ...loaded
      };
    }
    return defaultSettings;
  },

  update: (updates: Partial<AppSettings>): void => {
    const current = settingsStorage.load();
    settingsStorage.save({ ...current, ...updates });
  }
};

/**
 * Auth Storage
 */
export const authStorage = {
  saveToken: (token: AuthToken): void => {
    storage.set(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  loadToken: (): AuthToken | null => {
    return storage.get<AuthToken>(STORAGE_KEYS.AUTH_TOKEN);
  },

  clearToken: (): void => {
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    storage.remove(STORAGE_KEYS.USER);
  },

  saveUser: (user: any): void => {
    storage.set(STORAGE_KEYS.USER, user);
  },

  loadUser: (): any | null => {
    return storage.get(STORAGE_KEYS.USER);
  }
};

/**
 * Last Sync Timestamp
 */
export const syncStorage = {
  saveLastSync: (timestamp: string): void => {
    storage.set(STORAGE_KEYS.LAST_SYNC, timestamp);
  },

  loadLastSync: (): string | null => {
    return storage.get<string>(STORAGE_KEYS.LAST_SYNC);
  }
};

export default storage;
